import React from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Stack,
  Typography,
  Box,
  MenuItem,
  CircularProgress
} from '@mui/material';
import Editor from '@monaco-editor/react';
import MarkdownEditor from '../Common/MarkdownEditor';

function EditDialog({ 
  open, 
  example, 
  editingExample, 
  setEditingExample, 
  saving, 
  onSave, 
  onCancel 
}) {
  if (!editingExample) return null;

  return (
    <Dialog open={open} onClose={onCancel} maxWidth="md" fullWidth>
      <DialogTitle>编辑题目</DialogTitle>
      <DialogContent>
        <Stack spacing={3} sx={{ mt: 1 }}>
          {example?.questionType === 'code' ? (
            <MarkdownEditor
              label="题目描述（Description 标签页显示）"
              value={editingExample.question || ''}
              onChange={(value) => setEditingExample({ ...editingExample, question: value || '' })}
              minRows={4}
              maxRows={12}
              showPreview={true}
              showToolbar={true}
              placeholder="题意、要求、约束、输入输出说明；可用代码块展示辅助类（如 ListNode）..."
              disabled={saving}
            />
          ) : (
            <MarkdownEditor
              label="题目内容"
              value={editingExample.question || ''}
              onChange={(value) => setEditingExample({ ...editingExample, question: value || '' })}
              minRows={4}
              maxRows={10}
              showPreview={true}
              showToolbar={true}
              placeholder="输入题目内容（支持Markdown格式和图片）..."
              disabled={saving}
            />
          )}
          
          {example?.questionType === 'multiple_choice' && (
            <>
              <Typography variant="subtitle2">选项</Typography>
              {editingExample.options?.map((option, idx) => (
                <TextField
                  key={idx}
                  label={`选项 ${String.fromCharCode(65 + idx)}`}
                  fullWidth
                  multiline
                  rows={3}
                  value={option || ''}
                  onChange={(e) => {
                    const newOptions = [...(editingExample.options || [])];
                    newOptions[idx] = e.target.value;
                    setEditingExample({ ...editingExample, options: newOptions });
                  }}
                  onKeyDown={(e) => e.key === 'Enter' && e.stopPropagation()}
                  inputProps={{ style: { whiteSpace: 'pre-wrap' } }}
                />
              ))}
              <TextField
                label="正确答案"
                select
                fullWidth
                value={editingExample.correctAnswer || ''}
                onChange={(e) => setEditingExample({ ...editingExample, correctAnswer: e.target.value })}
              >
                {['A', 'B', 'C', 'D'].map((letter) => (
                  <MenuItem key={letter} value={letter}>{letter}</MenuItem>
                ))}
              </TextField>
            </>
          )}
          
          {example?.questionType === 'short_answer' && (
            <TextField
              label="参考答案"
              fullWidth
              multiline
              rows={3}
              value={editingExample.answer || ''}
              onChange={(e) => setEditingExample({ ...editingExample, answer: e.target.value })}
            />
          )}
          
          {example?.questionType === 'proof' && (
            <TextField
              label="证明步骤"
              fullWidth
              multiline
              rows={6}
              value={editingExample.proof || ''}
              onChange={(e) => setEditingExample({ ...editingExample, proof: e.target.value })}
            />
          )}
          
          {example?.questionType === 'code_tracing' && (
            <>
              <Box>
                <Typography variant="subtitle2" sx={{ mb: 1 }}>代码内容</Typography>
                <Box sx={{ border: 1, borderColor: 'divider', borderRadius: 1, overflow: 'hidden' }}>
                  <Editor
                    height="200px"
                    language="python"
                    value={editingExample.codeAnswer || ''}
                    onChange={(value) => setEditingExample({ ...editingExample, codeAnswer: value || '' })}
                    theme="light"
                    options={{
                      minimap: { enabled: false },
                      fontSize: 13,
                      lineNumbers: 'on',
                    }}
                  />
                </Box>
              </Box>
              {editingExample.options && editingExample.options.length > 0 && (
                <>
                  <Typography variant="subtitle2">选项（选择题模式）</Typography>
                  {editingExample.options.map((option, idx) => (
                    <TextField
                      key={idx}
                      label={`选项 ${String.fromCharCode(65 + idx)}`}
                      fullWidth
                      multiline
                      rows={3}
                      value={option || ''}
                      onChange={(e) => {
                        const newOptions = [...(editingExample.options || [])];
                        newOptions[idx] = e.target.value;
                        setEditingExample({ ...editingExample, options: newOptions });
                      }}
                      onKeyDown={(e) => e.key === 'Enter' && e.stopPropagation()}
                      inputProps={{ style: { whiteSpace: 'pre-wrap' } }}
                    />
                  ))}
                  <TextField
                    label="正确答案（选项字母，如 A）"
                    fullWidth
                    value={editingExample.correctAnswer || ''}
                    onChange={(e) => setEditingExample({ ...editingExample, correctAnswer: e.target.value })}
                  />
                </>
              )}
              {(!editingExample.options || editingExample.options.length === 0) && (
                <TextField
                  label="参考答案（简答题模式）"
                  fullWidth
                  multiline
                  rows={3}
                  value={editingExample.answer || ''}
                  onChange={(e) => setEditingExample({ ...editingExample, answer: e.target.value })}
                />
              )}
            </>
          )}
          
          {example?.questionType === 'code' && (
            <>
              <Box>
                <Typography variant="subtitle2" sx={{ mb: 1 }}>
                  代码模板（学生在 Code 标签页看到的骨架，仅放可运行代码：辅助类 + 要实现的类/函数骨架）
                </Typography>
                <Box sx={{ border: 1, borderColor: 'divider', borderRadius: 1, overflow: 'hidden' }}>
                  <Editor
                    height="220px"
                    language="python"
                    value={editingExample.codeAnswer || ''}
                    onChange={(value) => setEditingExample({ ...editingExample, codeAnswer: value || '' })}
                    theme="light"
                    options={{
                      minimap: { enabled: false },
                      fontSize: 13,
                      lineNumbers: 'on',
                    }}
                  />
                </Box>
              </Box>
              <Box>
                <Typography variant="subtitle2" sx={{ mb: 1 }}>公开测试用例</Typography>
                <Box sx={{ border: 1, borderColor: 'divider', borderRadius: 1, overflow: 'hidden' }}>
                  <Editor
                    height="150px"
                    language="python"
                    value={editingExample.publicTests || ''}
                    onChange={(value) => setEditingExample({ ...editingExample, publicTests: value || '' })}
                    theme="light"
                    options={{
                      minimap: { enabled: false },
                      fontSize: 13,
                      lineNumbers: 'on',
                    }}
                  />
                </Box>
              </Box>
              <Box>
                <Typography variant="subtitle2" sx={{ mb: 1 }}>秘密测试用例</Typography>
                <Box sx={{ border: 1, borderColor: 'divider', borderRadius: 1, overflow: 'hidden' }}>
                  <Editor
                    height="150px"
                    language="python"
                    value={editingExample.secretTests || ''}
                    onChange={(value) => setEditingExample({ ...editingExample, secretTests: value || '' })}
                    theme="light"
                    options={{
                      minimap: { enabled: false },
                      fontSize: 13,
                      lineNumbers: 'on',
                    }}
                  />
                </Box>
              </Box>
            </>
          )}
          
          <TextField
            label="解释"
            fullWidth
            multiline
            rows={3}
            value={editingExample.explanation || ''}
            onChange={(e) => setEditingExample({ ...editingExample, explanation: e.target.value })}
          />
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onCancel} disabled={saving}>
          取消
        </Button>
        <Button 
          onClick={onSave} 
          variant="contained" 
          disabled={saving}
          startIcon={saving ? <CircularProgress size={20} /> : null}
        >
          {saving ? '保存中...' : '保存'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

export default EditDialog;

